import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, IconButton, Link, Stack, TextField, Typography } from "@mui/material";
import { Headset, Link2, X } from "lucide-react";
import { pm } from "./api";
import { usePm } from "./PmContext";
import { T, TYPE, labelSx } from "../user/complaints/shared";

// Issue detail section: helpdesk tickets this issue was raised from / linked to.
// Tickets outside the viewer's helpdesk scope are counted but not shown.

const STATUS_TEXT = { resolved: "Resolved", closed: "Closed", pending: "Waiting on customer" };

export default function SupportTickets({ issueId, canEdit }) {
  const ctx = usePm();
  const [rows, setRows] = useState([]);
  const [hidden, setHidden] = useState(0);
  const [adding, setAdding] = useState(false);
  const [ticket, setTicket] = useState("");
  const load = useCallback(() => {
    pm.issueTickets(issueId).then((r) => {
      setRows(r.data || []);
      setHidden(r.hidden || 0);
    }).catch(() => setRows([]));
  }, [issueId]);
  useEffect(() => {
    load();
  }, [load]);
  const link = async () => {
    try {
      await pm.linkTicket(issueId, ticket.trim());
      setTicket("");
      setAdding(false);
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };
  if (!rows.length && !hidden && !canEdit) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ ...labelSx, flexGrow: 1 }}>Support tickets</Typography>
        {canEdit && !adding && <Button size="small" startIcon={<Link2 size={14} />} onClick={() => setAdding(true)}>Link ticket</Button>}
      </Stack>
      {adding && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField size="small" autoFocus placeholder="Ticket key, e.g. JW-10231" value={ticket} onChange={(e) => setTicket(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ticket.trim() && link()} />
          <Button size="small" variant="contained" disabled={!ticket.trim()} onClick={link}>Link</Button>
          <Button size="small" onClick={() => { setAdding(false); setTicket(""); }}>Cancel</Button>
        </Stack>
      )}
      {rows.map((t) => (
        <Stack key={t._id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, px: 1, borderRadius: "6px", "&:hover": { bgcolor: T.surfaceMuted } }}>
          <Headset size={14} color={T.inkMuted} />
          <Link component={RouterLink} to={`/helpdesk/tickets/${t._id}`} sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{t.key}</Link>
          <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{t.subject}{t.requester ? ` · ${t.requester}` : ""}</Typography>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, whiteSpace: "nowrap" }}>{STATUS_TEXT[t.statusType] || "Open"}</Typography>
          {canEdit && <IconButton size="small" onClick={() => pm.unlinkTicket(issueId, t._id).then(load).catch((err) => ctx.fail(err))}><X size={13} /></IconButton>}
        </Stack>
      ))}
      {hidden > 0 && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, px: 1 }}>{hidden} linked ticket{hidden === 1 ? "" : "s"} you cannot view in the helpdesk.</Typography>}
      {!rows.length && !hidden && !adding && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, px: 1 }}>No support tickets linked.</Typography>}
    </Box>
  );
}
